import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { MediaList, MediaUpload, useProfileImage, useSignatureImage } from 'helpers/ImageImports';
import { Doctor } from 'models/DoctorModel';

const DoctorForm: React.FC = () => {
  const [formId, formName] = [Code.Form.Id.DOCTOR, Code.Form.Name.DOCTOR];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [cityOptions, setCityOptions] = Hooks.Form.Options();
  const [genderOptions, setGenderOptions] = Hooks.Form.Options();
  const [submitterOptions, setSubmitterOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<Doctor>({});
  const [fileList, setFileList] = useState<MediaList>({});
  
  const [profileUrl, setProfileUrl] = useState<string | null>(null);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const {signatureImage, handleSignatureImageChange, handleAttachSignatureImage, handleDeleteSignatureImage } = useSignatureImage(fileList, setFileList, setSignatureUrl, setError);
  // const {profileImage, handleProfileImageChange, handleAttachProfileImage, handleDeleteProfileImage } = useProfileImage(fileList, setFileList, setProfileUrl, setError);
  
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.CITY, Code.Option.Common.NA, setCityOptions, setError);
    Files.Json.Http(Code.File.GENDER, Code.Option.Common.NA, setGenderOptions, setError);
    Files.Json.API(Code.File.SUBMITTER, Code.Option.Common.SELECT, setSubmitterOptions, setError);
   
    console.log("Doctor Data:", keyId);

    API.Data.GetByKeyId<Doctor>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        
        setActiveId(data.doctor_active); 
        Files.List.ParseFile(data.file_list, setFileList);
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
        }));
      }})
      setLoading(false);
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;

    const finalData = Utility.Sanitize.FormData({
      ...formData,
      company_id: Utility.Storage.CompanyId(),
      location_id: Utility.Storage.LocationId(),
      entry_id: Utility.Storage.UserId(),
      doctor_first_name: Utility.Convert.TitleCase(formData.doctor_first_name),
      doctor_middle_name: Utility.Convert.TitleCase(formData.doctor_middle_name),
      doctor_last_name: Utility.Convert.TitleCase(formData.doctor_last_name),
      doctor_phone: Utility.Convert.Number(formData.doctor_phone),
      file_list: JSON.stringify(Files.List.Clean(fileList)),
    });

    API.Data.Save(formName, keyId, finalData, setMessage)

  };

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} formName={formName} formId={formId} Privilege={Privilege} />
          <Control.Tab.Header titles={["Details", "Contact", "Images"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
            
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Text id={Name.Doctor.FirstName} value={formData.doctor_first_name} title={Title.FirstName} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} />
                <Field.Text id={Name.Doctor.MiddleName} value={formData.doctor_middle_name} title={Title.MiddleName} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Doctor.LastName} value={formData.doctor_last_name} title={Title.LastName} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Text id={Name.Doctor.ShortName} value={formData.doctor_short_name} title="Short Name" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Doctor.PractitionerCode} value={formData.doctor_practitioner_code} title="Practitioner ID" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Text id={Name.Doctor.PayToULI} value={formData.doctor_pay_to_uli} title="Pay to ULI" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Doctor.LocumArrangement} value={formData.doctor_locum_arrangement} title="Locum Arrangement" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Text id={Name.Doctor.SkillCode} value={formData.doctor_skill_code} title="Skill Code" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Doctor.FacilityNumber} value={formData.doctor_facility_number} title="Facility Number" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Text id={Name.Doctor.FunctionalCentre} value={formData.doctor_functional_centre} title="Functional Centre" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Doctor.PayToCode} value={formData.doctor_pay_to_code} title="Pay To Code" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Text id={Name.Doctor.BusinessArrangement} value={formData.doctor_business_arrangement} title="Business Arrangement" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Doctor.CoopCode} value={formData.doctor_coop_code} title="Co-Op ID" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Text id={Name.Doctor.LicenseNumber} value={formData.doctor_license_number} title="License Number" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Doctor.ShortCode} value={formData.doctor_short_code} title="Short Code" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Select id={Name.Submitter.Id} value={formData.submitter_id} title={"Submitter Name"} options={submitterOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[0,50,1]} require={true} />
              </Control.Row>
            </Control.Tab.Content>
            
            <Control.Tab.Content id={Code.Tab.Tab2} isActive={activeTab === Code.Tab.Tab2}>
              <Control.Row>
                <Field.Text id={Name.Doctor.Phone} value={Utility.Format.Phone(formData.doctor_phone)} title={Title.Phone} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Text id={Name.Doctor.Email} value={formData.doctor_email} title={Title.Email} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.Doctor.Address} value={formData.doctor_address} title={Title.Address} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.City.Id} value={formData.city_id} title={Title.City} options={cityOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[0,50,1]} require={true} />
                <Field.Text id={Name.Doctor.Postal} value={formData.doctor_postal} title={Title.Postal} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.Common.GenderId} value={formData.gender_id} title={Title.Gender} options={genderOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]}/>
                <Field.Color id={Name.Doctor.Color} value={formData.doctor_color} title={Title.Color} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
            </Control.Tab.Content>
            
            <Control.Tab.Content id={Code.Tab.Tab3} isActive={activeTab === Code.Tab.Tab3}>
              <MediaUpload label={Title.Signature} imageUrl={signatureUrl} handleImageChange={handleSignatureImageChange} handleAttachImage={handleAttachSignatureImage} handleDeleteImage={handleDeleteSignatureImage} summary={Info.SignSize} />
              {/* <MediaUpload label={Title.Profile} imageUrl={profileUrl} handleImageChange={handleProfileImageChange} handleAttachImage={handleAttachProfileImage} handleDeleteImage={handleDeleteProfileImage} summary={Info.ProfileSize} /> */}
            </Control.Tab.Content>

          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default DoctorForm;